/**
 * Application entry point — orchestrator.
 * Initializes the store, applies theme, builds layout shell,
 * mounts map, panels, and wires up variant switching.
 *
 * Layout: Header -> Sub-header -> Full-width map (with floating overlays) -> Bottom panels
 *
 * DOM construction lives in ./layout.ts, background services in ./services.ts.
 */

import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import { applyTheme, injectBaseStyles } from './theme';
import { registerServiceWorker, unregisterServiceWorkers } from './register-sw';
import { isTauriRuntime } from '@/utils/tauri';
import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { destroyMap } from '@/map/engine';
import { destroyGlobe } from '@/map/globe';
import { MapContainer, getMapContainer } from '@/map/map-container';
import { getStoredMapModePreference, preferenceToMapMode } from '@/map/map-mode';
import { initDarkShipCache, destroyDarkShipCache } from '@/map/dark-ship-cache';
import { mountMapControls } from '@/map/controls';
// Removed: coordinate-readout (analysts don't need lat/lng on hover)
// Removed: time-slider (only showed session time range, not useful)
import { destroyLayerSystem } from '@/map/layers/index';
import { destroyMapInteractions } from '@/map/interactions';
import { destroyTooltip } from '@/map/tooltip';
import { panelManager } from '@/panels/panel-manager';
import { createNewsFeedPanel } from '@/panels/news-feed-panel';
import { createSituationBoard } from '@/panels/situation-board';
import { createLayerControlPanel } from '@/panels/layer-control-panel';
import { createDetailPanel } from '@/panels/detail-panel';
import { createCountryPanel } from '@/panels/country-panel';
import { createCountryDossierPanel } from '@/panels/country-dossier-panel';
import { createSettingsPanel } from '@/panels/settings-panel';
import { createInsightsPanel } from '@/panels/insights-panel';
import { createStrategicPosturePanel } from '@/panels/strategic-posture-panel';
import { createCascadePanel } from '@/panels/cascade-panel';
import { createRiskPanel } from '@/panels/risk-panel';
import { createMonitorPanel } from '@/panels/monitor-panel';
import { createCIIDashboardPanel } from '@/panels/cii-dashboard-panel';
import { createSignalModal } from '@/panels/signal-modal';
import { createTelegramPanel } from '@/panels/telegram-panel';
import { createOrefPanel } from '@/panels/oref-panel';
import { createServiceStatusPanel } from '@/panels/service-status-panel';
import { createVerificationPanel } from '@/panels/verification-panel';
import { createDeductionPanel } from '@/panels/deduction-panel';
import { createAdvisoryPanel } from '@/panels/advisory-panel';
import { createUcdpPanel } from '@/panels/ucdp-panel';
import { createDisplacementPanel } from '@/panels/displacement-panel';
import { createCyberPanel } from '@/panels/cyber-panel';
import { createGpsjamPanel } from '@/panels/gpsjam-panel';
import { createCountryTimelinePanel } from '@/panels/country-timeline-panel';
import { createWorldClockPanel } from '@/panels/world-clock-panel';
import { createTradePolicyPanel } from '@/panels/trade-policy-panel';
import { createSupplyChainPanel } from '@/panels/supply-chain-panel';
import { createClimatePanel } from '@/panels/climate-panel';
import { createPredictionPanel } from '@/panels/prediction-panel';
import { createMarketPanel } from '@/panels/market-panel';
import { createEconomicPanel } from '@/panels/economic-panel';
import { createMacroSignalsPanel } from '@/panels/macro-signals-panel';
import { createGdeltIntelPanel } from '@/panels/gdelt-intel-panel';
import { destroyBrowserML } from '@/intelligence/ai/providers/browser-provider';
import { initDatabase } from '@/storage/database';
import { createEntityPanel } from '@/panels/entity-panel';
import { createInvestigationPanel, destroyInvestigationPanel } from '@/panels/investigation-panel';
import { createDigestPanel, destroyDigestPanel } from '@/panels/digest-panel';
import { getGraphPanel, destroyGraphPanel } from '@/panels/graph-panel';
import { entityStore } from '@/intelligence/entities/entity-store';
import { getInvestigationEntityIds } from '@/intelligence/investigation/investigation-store';
import { initInvestigationRunner } from '@/intelligence/investigation/investigation-runner';
import { initAnalysisBridge } from '@/intelligence/investigation/analysis-bridge';
import { findWeakMatches } from '@/intelligence/investigation/corroboration';
import { feedLoader } from '@/sources/feed-loader';
import { registerBuiltInSources } from '@/sources/built-in/index';
import { scheduler } from '@/core/scheduler';
import { getDeskById } from '@/config/desk-presets';
import { getDefaultVisibleLayers } from '@/config/layers';
import { initProviders } from '@/intelligence/ai/provider-registry';
import { setSidecarUrl } from '@/app/api-router';
import { migrateSecretsFromLocalStorage } from '@/storage/keychain';
import { initAnalysisEngine, getAnalysisEngine } from '@/intelligence/analysis/engine';
import { getCachedEvents } from '@/storage/indexeddb';
import { initNotifications, disposeNotifications } from '@/ui/components/native-notifications';
import { registerKnownIds, initPipelineListeners } from '@/core/pipeline';
import { initViewportDetection } from '@/utils/viewport';
import { destroyLabelDeconfliction } from '@/map/labels';
import { destroyCountryHighlight } from '@/map/country-highlight';
import { clearRefinementCache } from '@/intelligence/ai/classifier';
import { restoreStateFromURL, startURLSync } from '@/utils/url-state';
import { initChunkReloadHandler } from '@/app/chunk-reload';
import { initKeyboardShortcuts } from '@/utils/keyboard-shortcuts';
import { createSearchModal } from '@/ui/components/search-modal';
import { initActivityDots } from '@/ui/components/activity-dot';
import { initContextMenu } from '@/ui/components/context-menu';
import { initTvMode } from '@/ui/components/tv-mode';
import { startFreshnessMonitor } from '@/utils/data-freshness';
import { applyPanelOverrides } from '@/ui/components/panel-picker';
import { windowManager, getPopoutPanelId, initPopoutSync } from '@/app/window-manager';

import {
  buildHeader,
  buildSubHeader,
  buildFloatingLayers,
  buildBottomPanelGrid,
  showMigrationToast,
  showDeskSelectorModal,
  switchVariant,
  cleanupExportDropdown,
} from './layout';
import {
  loadPersistedTheme,
  loadPersistedDesk,
  isFirstLaunch,
  startClock,
  startOnlineMonitor,
  wireEventCount,
  wireSourceStatus,
} from './services';

import 'maplibre-gl/dist/maplibre-gl.css';

/**
 * Application-wide cleanup registry. Each entry is a teardown function
 * pushed during boot(). teardownApp() runs them all in reverse order.
 */
const _cleanupFns: (() => void)[] = [];

// Guard against multiple initialization (Vite HMR, module re-evaluation)
let booted = false;

/** Boot the application. */
function boot(): void {
  if (booted) return;
  booted = true;

  // Register deferred pipeline listeners (must happen after all modules are loaded
  // to avoid TDZ errors from circular imports in production bundles)
  initPipelineListeners();

  // Theme and styles
  const persistedTheme = loadPersistedTheme();
  appStore.setState({ theme: persistedTheme });
  injectBaseStyles();
  applyTheme(persistedTheme);
  initViewportDetection();

  // Chunk reload handler (detect stale code-split chunks)
  _cleanupFns.push(initChunkReloadHandler());

  // Restore state from URL query parameters (before layout/panel init)
  restoreStateFromURL();

  // Restore desk from localStorage (if URL didn't set one)
  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.has('desk') && !urlParams.has('variant')) {
    const savedDesk = loadPersistedDesk();
    if (savedDesk) {
      const desk = getDeskById(savedDesk);
      if (desk) {
        appStore.setState({ variant: savedDesk, activeLayers: desk.defaultLayers });
      }
    }
  }

  // PWA service worker (web only). On desktop, clean up registrations
  // left behind by builds that predate the isTauriRuntime() fix — a
  // stale cache-first worker would pin the webview to the old app shell.
  if (!isTauriRuntime()) {
    registerServiceWorker();
  } else {
    unregisterServiceWorkers();
  }

  const app = document.getElementById('app');
  if (!app) throw new Error('Missing #app element');
  app.innerHTML = '';

  // ── Build Layout Shell ──
  app.appendChild(buildHeader());
  app.appendChild(buildSubHeader());

  const mapRegion = document.createElement('div');
  mapRegion.id = 'map-region';
  const mapContainer = document.createElement('div');
  mapContainer.id = 'map-container';
  mapContainer.setAttribute('role', 'application');
  mapContainer.setAttribute('aria-label', 'Interactive map');
  mapRegion.appendChild(mapContainer);

  // Dedicated canvas host owned exclusively by MapContainer. Mode switches
  // wipe this element's children — controls, loader, and floating panels
  // live as siblings so they survive flat/globe switches.
  const mapCanvas = document.createElement('div');
  mapCanvas.id = 'map-canvas';
  mapContainer.appendChild(mapCanvas);

  // Map loading overlay
  const mapLoader = document.createElement('div');
  mapLoader.id = 'map-loader';
  mapLoader.style.cssText = `
    position: absolute; inset: 0; display: flex; align-items: center;
    justify-content: center; z-index: 5; background: var(--bg-primary);
    transition: opacity 300ms ease; pointer-events: none;
  `;
  const mapLoaderText = document.createElement('span');
  mapLoaderText.style.cssText = 'color: var(--text-muted); font-size: 13px;';
  mapLoaderText.textContent = 'Initializing map\u2026';
  mapLoader.appendChild(mapLoaderText);
  mapContainer.appendChild(mapLoader);

  const floatingLayers = buildFloatingLayers();
  mapContainer.appendChild(floatingLayers);

  const overlayRight = document.createElement('div');
  overlayRight.id = 'overlay-right';
  mapContainer.appendChild(overlayRight);

  app.appendChild(mapRegion);
  app.appendChild(buildBottomPanelGrid());

  // ── Initialize Map via MapContainer adapter ──
  mountMapControls(mapContainer);
  const mapContainerAdapter = new MapContainer({
    container: mapCanvas,
    initialMode: preferenceToMapMode(getStoredMapModePreference()),
  });
  _cleanupFns.push(() => mapContainerAdapter.destroy());
  void mapContainerAdapter.init().then(() => {
    const loader = document.getElementById('map-loader');
    if (loader) {
      loader.style.opacity = '0';
      setTimeout(() => loader.remove(), 300);
    }
  });
  // Coordinate readout and time slider removed — not useful for analysts

  // Default layers from active desk preset
  if (appStore.getState().activeLayers.length === 0) {
    const activeDesk = getDeskById(appStore.getState().variant);
    const layers = activeDesk?.defaultLayers ?? getDefaultVisibleLayers();
    appStore.setState({ activeLayers: layers });
  }

  // ── Mount Panels ──
  const layerControl = createLayerControlPanel();
  const floatingBody = floatingLayers.querySelector('.floating-layers-body');
  if (floatingBody) floatingBody.appendChild(layerControl);

  // Mount Situation Board into the "situation" tab page
  const { element: sitBoardEl, board: sitBoard } = createSituationBoard();
  const sitPage = document.querySelector('#bottom-panel-left [data-tab-page="situation"]');
  if (sitPage) sitPage.appendChild(sitBoardEl);

  // Wire up the badge element
  const sitBadge = document.getElementById('situation-badge');
  if (sitBadge) sitBoard.setBadgeElement(sitBadge);

  // Mount News Feed into the "news" tab page
  const newsFeed = createNewsFeedPanel();
  const newsPage = document.querySelector('#bottom-panel-left [data-tab-page="news"]');
  if (newsPage) newsPage.appendChild(newsFeed);

  // Mount Investigation Panel into right column (WP-S13.1)
  const invPanelEl = createInvestigationPanel();
  const invMount = document.getElementById('bottom-investigation-mount');
  if (invMount) invMount.appendChild(invPanelEl);

  // ── Overlay panels (lazy — factory called on first showPanel()) ──
  panelManager.registerPanel('detail', { title: 'Event Detail', icon: '', position: 'overlay', factory: () => createDetailPanel(), defaultVisible: false });
  panelManager.registerPanel('country', { title: 'Country', icon: '', position: 'overlay', factory: () => createCountryPanel(), defaultVisible: false });
  panelManager.registerPanel('country-dossier', { title: 'Country Dossier', icon: '', position: 'overlay', factory: () => createCountryDossierPanel(), defaultVisible: false });
  panelManager.registerPanel('settings', { title: 'Settings', icon: '', position: 'overlay', factory: () => createSettingsPanel(), defaultVisible: false });

  // Intelligence panels (lazy)
  panelManager.registerPanel('insights', { title: 'AI Insights', icon: '', position: 'overlay', factory: () => createInsightsPanel(), defaultVisible: false });
  panelManager.registerPanel('strategic-posture', { title: 'Strategic Posture', icon: '', position: 'overlay', factory: () => createStrategicPosturePanel(), defaultVisible: false });
  panelManager.registerPanel('cascade', { title: 'Cascade Analysis', icon: '', position: 'overlay', factory: () => createCascadePanel(), defaultVisible: false });
  panelManager.registerPanel('risk', { title: 'Risk Ranking', icon: '', position: 'overlay', factory: () => createRiskPanel(), defaultVisible: false });
  panelManager.registerPanel('monitor', { title: 'Keyword Monitor', icon: '', position: 'overlay', factory: () => createMonitorPanel(), defaultVisible: false });
  panelManager.registerPanel('cii-dashboard', { title: 'CII Dashboard', icon: '', position: 'overlay', factory: () => createCIIDashboardPanel(), defaultVisible: false });
  panelManager.registerPanel('signal-detail', { title: 'Signal Detail', icon: '', position: 'overlay', factory: () => createSignalModal(), defaultVisible: false });
  panelManager.registerPanel('entity', { title: 'Entities', icon: '', position: 'overlay', factory: () => createEntityPanel(), defaultVisible: false });
  panelManager.registerPanel('digest', { title: 'Intelligence Digest', icon: '', position: 'overlay', factory: () => createDigestPanel().element, defaultVisible: false });
  panelManager.registerPanel('deduction', { title: 'Deduction Chain', icon: '', position: 'overlay', factory: () => createDeductionPanel(), defaultVisible: false });

  // Geopolitical panels (lazy)
  panelManager.registerPanel('telegram', { title: 'Telegram OSINT', icon: '', position: 'overlay', factory: () => createTelegramPanel(), defaultVisible: false });
  panelManager.registerPanel('oref', { title: 'OREF Sirens', icon: '', position: 'overlay', factory: () => createOrefPanel(), defaultVisible: false });

  // GDELT Intelligence panel (lazy)
  panelManager.registerPanel('gdelt-intel', { title: 'GDELT Intelligence', icon: '', position: 'overlay', factory: () => createGdeltIntelPanel(), defaultVisible: false });

  // Geopolitical panels (lazy)
  panelManager.registerPanel('advisory', { title: 'Travel Advisories', icon: '', position: 'overlay', factory: () => createAdvisoryPanel(), defaultVisible: false });
  panelManager.registerPanel('ucdp', { title: 'Armed Conflicts (UCDP)', icon: '', position: 'overlay', factory: () => createUcdpPanel(), defaultVisible: false });
  panelManager.registerPanel('displacement', { title: 'Displacement Flows', icon: '', position: 'overlay', factory: () => createDisplacementPanel(), defaultVisible: false });
  panelManager.registerPanel('trade-policy', { title: 'Trade Policy', icon: '', position: 'overlay', factory: () => createTradePolicyPanel(), defaultVisible: false });

  // Infrastructure panels (lazy)
  panelManager.registerPanel('service-status', { title: 'Service Status', icon: '', position: 'overlay', factory: () => createServiceStatusPanel(), defaultVisible: false });
  panelManager.registerPanel('cyber', { title: 'Cyber Threats', icon: '', position: 'overlay', factory: () => createCyberPanel(), defaultVisible: false });
  panelManager.registerPanel('gpsjam', { title: 'GPS Interference', icon: '', position: 'overlay', factory: () => createGpsjamPanel(), defaultVisible: false });
  panelManager.registerPanel('supply-chain', { title: 'Supply Chain', icon: '', position: 'overlay', factory: () => createSupplyChainPanel(), defaultVisible: false });

  // Environmental panels (lazy)
  panelManager.registerPanel('climate', { title: 'Climate Anomalies', icon: '', position: 'overlay', factory: () => createClimatePanel(), defaultVisible: false });

  // Finance panels (lazy)
  panelManager.registerPanel('prediction', { title: 'Prediction Markets', icon: '', position: 'overlay', factory: () => createPredictionPanel(), defaultVisible: false });
  panelManager.registerPanel('market', { title: 'Markets', icon: '', position: 'overlay', factory: () => createMarketPanel(), defaultVisible: false });
  panelManager.registerPanel('economic', { title: 'Economic Indicators', icon: '', position: 'overlay', factory: () => createEconomicPanel(), defaultVisible: false });
  panelManager.registerPanel('macro-signals', { title: 'Macro Signals', icon: '', position: 'overlay', factory: () => createMacroSignalsPanel(), defaultVisible: false });

  // Tools panels (lazy)
  panelManager.registerPanel('verification', { title: 'Verification', icon: '', position: 'overlay', factory: () => createVerificationPanel(), defaultVisible: false });
  panelManager.registerPanel('country-timeline', { title: 'Country Timeline', icon: '', position: 'overlay', factory: () => createCountryTimelinePanel(), defaultVisible: false });
  panelManager.registerPanel('world-clock', { title: 'World Clock', icon: '', position: 'overlay', factory: () => createWorldClockPanel(), defaultVisible: false });

  // Apply panel enablement overrides before mounting (WP-10.1)
  applyPanelOverrides(appStore.getState().variant);

  panelManager.mountAll();

  // ── Mount Search Modal (full-screen overlay, outside panel manager) ──
  const { element: searchEl, modal: searchModal } = createSearchModal();
  app.appendChild(searchEl);
  _cleanupFns.push(() => searchModal.destroy());

  // ── First-launch desk selector (non-blocking — app boots behind it) ──
  if (isFirstLaunch() && !urlParams.has('desk') && !urlParams.has('variant')) {
    showDeskSelectorModal().then((deskId) => {
      switchVariant(deskId);
      console.log('[Sentinel] First launch — selected desk:', deskId);
    });
  }

  // ── Popout mode: secondary Tauri window showing a single panel (WP-10.4) ──
  const popoutId = getPopoutPanelId();
  if (popoutId) {
    _cleanupFns.push(initPopoutSync());
    // Hide main UI, show only the requested panel
    const headerBar = document.getElementById('header-bar');
    const subHeader = document.getElementById('sub-header-bar');
    const mapRegionEl = document.getElementById('map-region');
    const bottomGrid = document.getElementById('bottom-panel-grid');
    if (headerBar) headerBar.style.display = 'none';
    if (subHeader) subHeader.style.display = 'none';
    if (bottomGrid) bottomGrid.style.display = 'none';
    // Move overlay out of map-region before hiding it (panels are mounted inside overlay-right)
    const overlayEl = document.getElementById('overlay-right');
    if (overlayEl && app) {
      app.appendChild(overlayEl);
      overlayEl.style.cssText = 'position:static; width:100%; height:100vh; pointer-events:auto;';
    }
    if (mapRegionEl) mapRegionEl.style.display = 'none';
    panelManager.showPanel(popoutId);
    console.log('[Sentinel] Popout mode — panel:', popoutId);
  }

  // ── Multi-window manager (WP-10.4 — desktop only, main window) ──
  if (!popoutId) {
    _cleanupFns.push(windowManager.init());
  }

  // ── Start Services ──
  _cleanupFns.push(startClock());
  _cleanupFns.push(startOnlineMonitor());
  _cleanupFns.push(wireEventCount());
  _cleanupFns.push(wireSourceStatus());
  _cleanupFns.push(initKeyboardShortcuts());
  _cleanupFns.push(startFreshnessMonitor());
  _cleanupFns.push(initActivityDots());
  _cleanupFns.push(initContextMenu());
  _cleanupFns.push(initTvMode());
  _cleanupFns.push(eventBus.on('event:selected', () => panelManager.showPanel('detail')));
  _cleanupFns.push(eventBus.on('focal-point:selected', () => panelManager.showPanel('detail')));
  _cleanupFns.push(eventBus.on('country:selected', () => panelManager.showPanel('country')));
  _cleanupFns.push(eventBus.on('signal:inspect', () => panelManager.showPanel('signal-detail')));
  _cleanupFns.push(eventBus.on('entity:selected', () => panelManager.showPanel('entity')));

  // Investigation graph navigation (WP-S13.7)
  _cleanupFns.push(eventBus.on('investigation:open-graph', async (investigation) => {
    try {
      // Load entities linked to this investigation
      const entityIds = await getInvestigationEntityIds(investigation.id);
      const entities = (await Promise.all(
        entityIds.map(id => entityStore.getById(id)),
      )).filter((e): e is import('@/intelligence/entities/types').Entity => e !== null);

      // Gather all relationships for these entities
      const relSets = await Promise.all(
        entityIds.map(id => entityStore.getRelationships(id)),
      );
      // Deduplicate relationships by ID
      const relMap = new Map<string, import('@/intelligence/entities/types').Relationship>();
      for (const rels of relSets) {
        for (const rel of rels) {
          relMap.set(rel.id, rel);
        }
      }
      const relationships = Array.from(relMap.values());

      // Find weak matches for merge UI
      const weakMatches = findWeakMatches(entities);

      const graphPanel = getGraphPanel();
      graphPanel.open(investigation, entities, relationships, () => {
        // Returned to dashboard — no special action needed
      }, weakMatches);
    } catch (err) {
      console.error('[Sentinel] Failed to open graph view:', err);
    }
  }));

  // Update investigation header pulse (WP-S13.1)
  _cleanupFns.push(eventBus.on('investigation:start', () => {
    const pulse = document.getElementById('inv-header-pulse');
    if (pulse) {
      pulse.style.display = 'block';
      pulse.style.animation = 'inv-pulse 1s infinite';
    }
  }));
  _cleanupFns.push(eventBus.on('investigation:complete', (investigation) => {
    const pulse = document.getElementById('inv-header-pulse');
    if (pulse) {
      pulse.style.display = 'none';
      pulse.style.animation = 'none';
    }
    // Auto-open the graph panel when investigation completes
    eventBus.emit('investigation:open-graph', investigation);
  }));
  _cleanupFns.push(eventBus.on('investigation:error', () => {
    const pulse = document.getElementById('inv-header-pulse');
    if (pulse) {
      pulse.style.display = 'none';
      pulse.style.animation = 'none';
    }
  }));

  // AI providers (fire-and-forget)
  initProviders().catch((err) => console.warn('[Sentinel] AI provider init failed:', err));

  // Database initialization (SQLite on Tauri, IndexedDB on web)
  initDatabase().catch((err) => console.warn('[Sentinel] Database init failed:', err));

  // Investigation runner (loads desk configs, listens for investigation:request events)
  initInvestigationRunner().then((cleanup) => {
    _cleanupFns.push(cleanup);
  }).catch((err) => console.warn('[Sentinel] Investigation runner init failed:', err));

  // Analysis bridge — wires Phase 1 analysis signals into active investigations
  initAnalysisBridge();

  // Desktop: migrate secrets, connect sidecar, check updates
  if (isTauriRuntime()) {
    migrateSecretsFromLocalStorage().then((migrated) => {
      if (migrated) showMigrationToast();
    }).catch((err) => console.warn('[Sentinel] Secret migration failed:', err));

    import('./updater').then(({ initUpdateChecker }) => initUpdateChecker());

    import('@tauri-apps/api/core').then(({ invoke }) => {
      invoke<number>('get_sidecar_port').then((port) => {
        setSidecarUrl(`http://127.0.0.1:${port}`);
        console.log('[Sentinel] Sidecar connected on port', port);
      });
    }).catch((err) => console.warn('[Sentinel] Failed to get sidecar port:', err));
  }

  // Data collection and analysis
  registerBuiltInSources().then(async () => {
    // Start scheduler with deferred fetches — let the map render with static/cached data first
    scheduler.startAll({ deferInitialFetch: true });
    console.log('[Sentinel] Collection scheduler started (deferred)');

    // Warm start: merge cached events BEFORE analysis engine starts
    // so the first analysis tick (5s) has the full cached dataset
    try {
      const cached = await getCachedEvents();
      if (cached.length > 0) {
        const cutoff = Date.now() - 48 * 60 * 60 * 1000; // 48h staleness limit
        const events = cached
          .map((r) => r.data as import('@/sources/base-source').NormalizedEvent)
          .filter((ev) => {
            const ts = ev._timestampMs ?? new Date(ev.timestamp).getTime();
            if (!Number.isFinite(ts) || ts < cutoff) return false;
            // Reject corrupted events with JSON titles (pipeline artifacts from demo provider)
            const title = ev.title?.trim();
            if (!title || title.startsWith('{') || title.startsWith('[')) return false;
            return true;
          });

        if (events.length > 0) {
          const current = appStore.getState().events;
          const existingIds = new Set(current.map((e) => e.id));
          const newFromCache = events.filter((e) => !existingIds.has(e.id));
          if (newFromCache.length > 0) {
            appStore.setState({ events: [...current, ...newFromCache] });
            registerKnownIds(newFromCache.map((ev) => ev.id));
          }
          console.log(`[Sentinel] Merged ${newFromCache.length} cached events (warm start)`);
        }
      }
    } catch (err) {
      console.warn('[Sentinel] Failed to restore cached events:', err);
    }

    // Now start the analysis engine — cached events are in the store
    initAnalysisEngine();
    // Dark-ship anomalies are emitted once per dark period — the cache
    // must outlive flat/globe renderer switches, so it lives app-wide.
    initDarkShipCache();
    initNotifications();

    // Flush deferred source fetches after map has had time to render
    setTimeout(() => {
      scheduler.flushAll();
      console.log('[Sentinel] Deferred collection flush started');
    }, 2_000);
  }).catch((err) => console.error('[Sentinel] Failed to register built-in sources:', err));

  // Load feeds for active desk preset
  const activeDesk = getDeskById(appStore.getState().variant);
  if (activeDesk) {
    feedLoader.loadForCategories(activeDesk.feedCategories);
  } else {
    feedLoader.loadForVariant(appStore.getState().variant);
  }
  _cleanupFns.push(eventBus.on('variant:changed', ({ to }) => {
    const desk = getDeskById(to);
    if (desk) {
      feedLoader.loadForCategories(desk.feedCategories);
    } else {
      feedLoader.loadForVariant(to);
    }
    // Re-apply panel enablement for the new desk (WP-10.1)
    applyPanelOverrides(to);
  }));

  // URL state sync (debounced writes on map move / state changes)
  _cleanupFns.push(startURLSync());

  console.log('[Sentinel] Initialized — desk:', appStore.getState().variant);

  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).__SENTINEL_STORE__ = appStore;
  }
}

/**
 * Tear down the application — release all resources in reverse init order.
 */
function teardownApp(): void {
  feedLoader.stop();
  scheduler.stopAll();
  const engine = getAnalysisEngine();
  if (engine) engine.stop();
  clearRefinementCache();

  for (let i = _cleanupFns.length - 1; i >= 0; i--) {
    try { _cleanupFns[i](); } catch { /* ignore teardown errors */ }
  }
  _cleanupFns.length = 0;

  const panelEls = document.querySelectorAll<HTMLElement & { destroy?: () => void }>('[class$="-panel"], .news-feed, .layer-control');
  panelEls.forEach(el => el.destroy?.());

  destroyInvestigationPanel();
  destroyDigestPanel();
  destroyGraphPanel();
  disposeNotifications();
  destroyBrowserML();
  destroyCountryHighlight();
  destroyLabelDeconfliction();
  destroyDarkShipCache();
  // MapContainer.destroy() handles: interactions, layers, tooltip, globe, map
  const mc = getMapContainer();
  if (mc) {
    mc.destroy();
  } else {
    destroyMapInteractions();
    destroyLayerSystem();
    destroyTooltip();
    destroyGlobe();
    destroyMap();
  }
  cleanupExportDropdown();

  const app = document.getElementById('app');
  if (app) app.innerHTML = '';
  console.log('[Sentinel] Teardown complete');
}

// Global error boundary
window.addEventListener('error', (e) => {
  console.error('[Sentinel] Uncaught error:', e.error ?? e.message);
  const statusDot = document.getElementById('status-dot');
  if (statusDot) {
    statusDot.className = 'status-dot offline';
    statusDot.title = 'Error';
  }
});

window.addEventListener('unhandledrejection', (e) => {
  console.error('[Sentinel] Unhandled rejection:', e.reason);
  e.preventDefault();
});

window.addEventListener('beforeunload', () => {
  if (booted) teardownApp();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (booted) teardownApp();
    booted = false;
  });
}
